import { motion, useReducedMotion } from "framer-motion";
import { Reveal, SectionTag, EASE, MaskedLines } from "@/components/Reveal";

const LAYERS = [
  { title: "Customer Acquisition", sub: "Search, digital channels and customer enquiries" },
  { title: "Digital Experience", sub: "Web platforms, forms and customer journeys" },
  { title: "CRM & Workflows", sub: "Lead management, communication and operational coordination" },
  { title: "Service Operations", sub: "Service-provider networks, allocation, fulfilment and support" },
  { title: "Payments & Commercial Systems", sub: "Digital payments, transaction workflows and commercial processes" },
  { title: "Data & Improvement", sub: "Performance visibility, customer feedback and continuous optimization" },
];

export default function OperatingModel() {
  const reduce = useReducedMotion();
  return (
    <section
      id="operating-model"
      data-testid="operating-model-section"
      className="scroll-mt-20 border-t border-white/5 bg-[#0B0A07] py-20 sm:py-24"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <div>
          <Reveal>
            <SectionTag index="05">Our Operating Model</SectionTag>
          </Reveal>
          <h2
            data-testid="operating-model-headline"
            className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
          >
            <MaskedLines delay={0.1}>
              Technology connects
              <br />
              <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
                every part of the business.
              </span>
            </MaskedLines>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-xl text-base font-normal leading-relaxed text-slate-300 sm:text-lg">
              Our businesses are built around connected systems that bring
              customer acquisition, service operations, payments, support and
              data into one operating model.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex items-center gap-4">
              <span className="h-2 w-2 rotate-45 bg-[#D4AF37]" aria-hidden="true" />
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-stone-500">
                One foundation — every business we run
              </p>
            </div>
          </Reveal>
        </div>

        {/* connected vertical system: six layers -> operating business */}
        <Reveal delay={0.2}>
          <div data-testid="operating-model-layers" className="relative pl-8">
            <motion.span
              className="absolute bottom-2 left-[5px] top-2 w-px origin-top bg-gradient-to-b from-[#E7CE8B]/70 via-[#D4AF37]/40 to-[#D4AF37]/60"
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 2, ease: EASE }}
              aria-hidden="true"
            />
            <div className="space-y-2.5">
              {LAYERS.map((layer, i) => (
                <Reveal key={layer.title} delay={0.1 + i * 0.12}>
                  <div className="group relative border border-white/10 bg-white/[0.03] px-6 py-4 transition-colors duration-500 hover:border-[#D4AF37]/50 hover:bg-white/[0.05]">
                    <span
                      className="absolute -left-8 top-1/2 h-[7px] w-[7px] -translate-y-1/2 rotate-45 border border-[#D4AF37]/60 bg-[#0B0A07] transition-colors duration-500 group-hover:bg-[#D4AF37]"
                      aria-hidden="true"
                    />
                    <div className="flex items-baseline gap-4">
                      <span className="shrink-0 text-xs font-medium tracking-[0.25em] text-[#D4AF37]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium uppercase tracking-[0.15em] text-slate-200">
                          {layer.title}
                        </p>
                        <p className="mt-1 text-xs font-normal leading-relaxed text-stone-400">
                          {layer.sub}
                        </p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}

              {/* terminal node */}
              <motion.div
                data-testid="operating-business-node"
                className="relative !mt-6 border border-[#D4AF37]/45 bg-[#D4AF37]/[0.07] px-6 py-4"
                initial={reduce ? {} : { opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
              >
                <span
                  className="absolute -left-8 top-1/2 h-[9px] w-[9px] -translate-y-1/2 rotate-45 bg-[#E7CE8B]"
                  aria-hidden="true"
                />
                <div className="flex items-baseline gap-4">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#E7CE8B]" aria-hidden="true" />
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E7CE8B]">
                    Operating Business
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
